import {
    Autocomplete,
    AutocompleteChangeReason,
    AutocompleteCloseReason,
    AutocompleteRenderOptionState,
    Chip,
    Popper,
    PopperPlacementType,
    TextField,
    Theme,
    createFilterOptions,
} from '@mui/material';
import { FocusEventHandler, HTMLAttributes, ReactNode, SyntheticEvent, useEffect, useMemo, useRef } from 'react';
import { ObjectWithId } from '@synergy/models';
import { debounce } from 'lodash';
import { makeStyles } from '@mui/styles';
import { useIntl } from 'react-intl';

// A field that holds chips is as tall as the chips it holds. The theme says
// how big a chip is; these are the two sizes that follow from it — the legacy
// one for the shop and the customer app, the dense one for a PALO screen,
// where a 52-pixel box stood beside 29-pixel fields.
const LEGACY = { chipHeight: 32, chipPadding: 8, gap: '8px', inset: '12px 52px 12px 14px !important', minHeight: 52 };
const DENSE = { chipHeight: 19, chipPadding: 2, gap: '4px', inset: '4px 34px 4px 9px !important', minHeight: 29 };

const useStyles = makeStyles((theme: Theme) => {
    const chip = theme.chip;
    const dense = chip !== undefined && chip.labelMinWidth === 0;
    const size = dense ? DENSE : LEGACY;

    return {
        chip: {
            fontSize: chip?.fontSize ?? 12,
            fontWeight: chip?.fontWeight ?? 'bold',
            height: size.chipHeight,
            padding: size.chipPadding,
            borderRadius: chip?.borderRadius ?? 18,

            '& svg': {
                color: 'rgba(255, 255, 255, 0.7)',
                margin: '0px 4px 0px 0px !important',
            },
            '& > span': {
                padding: dense ? '0 4px' : '0 8px',
                textAlign: 'center',
            },
        },
        inputContainer: {
            padding: size.inset,
            minHeight: size.minHeight,
            flexWrap: 'wrap',
            gap: size.gap,

            '& button': {
                marginRight: dense ? 0 : 4,
                padding: dense ? 3 : 12,
            },
        },
        input: {
            height: size.chipHeight,
            padding: 0,
            flexGrow: 1,
            textOverflow: 'ellipsis',
            minWidth: 30,
            width: 0,
        },
    };
});

export interface ChipAutocompleteProps<T extends ObjectWithId> {
    options: T[];
    value: T[];
    getOptionLabel: (value: T) => string;
    onChange: (event: SyntheticEvent<Element, Event>, value: T[], reason: AutocompleteChangeReason) => void;
    getChipLabel: (option: T) => string;

    required?: boolean;
    popperPlacement?: PopperPlacementType | undefined;
    error?: boolean;
    helperText?: ReactNode;
    noOptionsText?: ReactNode;
    textfieldPlaceholder?: string;
    textfieldMaxWidth?: number;
    chipColor?: 'primary' | 'secondary';
    disabled?: boolean;
    limitTags?: number;
    renderOption?: (option: T, state: AutocompleteRenderOptionState) => ReactNode;
    onTextFieldValueChange?: (event: SyntheticEvent<Element, Event>, value: string) => void;
    onClose?: (event: SyntheticEvent<Element, Event>, reason: AutocompleteCloseReason) => void;
    onOpen?: () => void;
    onBlur?: FocusEventHandler | undefined;
    isOptionEqualToValue?: (option: T, value: T) => boolean;
    debounceTime?: number;
    /**
     * Offers "add all" above the suggestions and keeps the list open while
     * picking, so a set of tags is assigned in one go rather than one opening at
     * a time (key-user brief A3.1). Adds exactly the suggestions shown — narrow
     * them by typing first.
     */
    selectAll?: boolean;
}

/** Stands in the option list for "add all shown"; never part of a value. */
const SELECT_ALL_ID = '__select-all__';

export const ChipAutocomplete = <T extends ObjectWithId>(props: ChipAutocompleteProps<T>) => {
    const classes = useStyles();
    const intl = useIntl();

    // The suggestions currently on screen, which is what "add all" adds.
    const shown = useRef<T[]>([]);
    const defaultFilter = useMemo(() => createFilterOptions<T>(), []);
    const selectAllOption = useMemo(() => ({ id: SELECT_ALL_ID } as unknown as T), []);
    const isSelectAll = (option: T): boolean => (option as ObjectWithId).id === (SELECT_ALL_ID as unknown);

    // create a debounced version of onTextFieldValueChange
    const debouncedInputChange = useMemo(() => {
        if (!props.onTextFieldValueChange) return undefined;
        return debounce(props.onTextFieldValueChange, props.debounceTime ?? 300);
    }, [props.onTextFieldValueChange, props.debounceTime]);

    // cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedInputChange?.cancel();
        };
    }, [debouncedInputChange]);

    const internalRenderOption = (
        liProps: HTMLAttributes<HTMLLIElement>,
        option: T,
        state: AutocompleteRenderOptionState
    ) => {
        if (isSelectAll(option)) {
            return (
                <li {...liProps} key={SELECT_ALL_ID} style={{ fontWeight: 600 }}>
                    {intl.formatMessage(
                        { id: 'app.common.addAllShown', defaultMessage: 'Alle {count} hinzufügen' },
                        { count: shown.current.length }
                    )}
                </li>
            );
        }
        if (props.renderOption) {
            return (
                <li {...liProps} key={option.id}>
                    {props.renderOption(option, state)}
                </li>
            );
        }
        return (
            <li {...liProps} key={option.id}>
                {props.getOptionLabel(option)}
            </li>
        );
    };

    return (
        <Autocomplete
            multiple={true}
            limitTags={props.limitTags || 2}
            forcePopupIcon={false}
            filterSelectedOptions={true}
            disableCloseOnSelect={props.selectAll}
            noOptionsText={props.noOptionsText}
            options={props.options}
            filterOptions={
                props.selectAll
                    ? (options, state) => {
                          const filtered = defaultFilter(options, state);
                          // Options are often fresh objects per fetch, so the
                          // reference check behind filterSelectedOptions lets
                          // assigned ones through; they must not be added twice.
                          const assigned = new Set(props.value.map(option => option.id));
                          shown.current = filtered.filter(option => !assigned.has(option.id));

                          return shown.current.length > 1 ? [selectAllOption, ...filtered] : filtered;
                      }
                    : undefined
            }
            getOptionLabel={option => (isSelectAll(option) ? '' : props.getOptionLabel(option))}
            renderOption={internalRenderOption}
            value={props.value}
            onBlur={props.onBlur}
            renderTags={(value: T[], getProps) =>
                value.map((option: T, index: number) => (
                    <Chip
                        {...getProps({ index })}
                        color={props.chipColor ?? 'primary'}
                        label={props.getChipLabel(option)}
                        // The whole name where a narrow field cuts it (R-178).
                        title={props.getChipLabel(option)}
                        className={classes.chip}
                    />
                ))
            }
            renderInput={params => (
                <TextField
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        className: classes.inputContainer,
                        style: { maxWidth: props.textfieldMaxWidth },
                    }}
                    inputProps={{
                        ...params.inputProps,
                        className: classes.input,
                    }}
                    placeholder={props.textfieldPlaceholder && intl.formatMessage({ id: props.textfieldPlaceholder })}
                    required={props.required}
                    error={props.error}
                    helperText={props.helperText}
                />
            )}
            onChange={(event, value, reason) => {
                if (props.selectAll && value.some(isSelectAll)) {
                    const kept = value.filter(option => !isSelectAll(option));
                    props.onChange(event, [...kept, ...shown.current], 'selectOption');

                    return;
                }
                props.onChange(event, value, reason);
            }}
            onInputChange={debouncedInputChange}
            onClose={props.onClose}
            onOpen={props.onOpen}
            disabled={props.disabled}
            isOptionEqualToValue={props.isOptionEqualToValue}
            PopperComponent={({ children, ...popperProps }) => (
                <Popper {...popperProps} placement={props.popperPlacement}>
                    {children}
                </Popper>
            )}
        />
    );
};
